import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Rating from "@mui/material/Rating";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardMedia from "@mui/material/CardMedia";
import Dialog from "@mui/material/Dialog";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ShareIcon from "@mui/icons-material/Share";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import VerifiedIcon from "@mui/icons-material/Verified";
import StarIcon from "@mui/icons-material/Star";
import CloseIcon from "@mui/icons-material/Close";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { getProductInfo } from "../../../../services/Public/PublicPagesApi";
import { getImageUrl, MEDIA_BASE_URL } from "../../../../services/indexMediaBaseUrl";
import { saveProduct, createReview } from "../../../../services/UserApi/UserApi";
import AddToCartDialog from "../../../../components/AddToCartDialog";
import ShareUrlDialog from "./ShareUrlDialog";
import { useTranslation } from "react-i18next";

export default function ProductInfo({ productId: propProductId }) {
  const { id: paramId } = useParams();
  const productId = propProductId || paramId;
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("product_info");

  let lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
  if (lang === "rn") lang = "en";
  lang = lang.startsWith("ar") ? "ar" : "en";
  const isAr = lang === "ar";

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [product, setProduct] = useState(null);
  const [reviewsSummary, setReviewsSummary] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [relatedCategory, setRelatedCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewSuccess, setReviewSuccess] = useState("");
  const [reviewError, setReviewError] = useState("");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [addCartOpen, setAddCartOpen] = useState(false);
  const [addCartProduct, setAddCartProduct] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const openLightbox = (index = 0) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };
  const closeLightbox = () => setLightboxOpen(false);
  const prevLightbox = () => setLightboxIndex((i) => (i > 0 ? i - 1 : images.length - 1));
  const nextLightbox = () => setLightboxIndex((i) => (i < images.length - 1 ? i + 1 : 0));

  const handleSave = async () => {
    setSaveError("");
    const userRaw = localStorage.getItem("user");
    if (!userRaw) {
      navigate("/login");
      return;
    }
    const pid = product?.id || productId;
    if (!pid || saving) return;
    setSaving(true);
    try {
      await saveProduct(pid);
      setSaved(true);
    } catch (err) {
      setSaveError(err?.response?.data?.message || err?.message || t("save_failed", { defaultValue: "فشل الحفظ" }));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitReview = async () => {
    setReviewError("");
    setReviewSuccess("");
    const userRaw = localStorage.getItem("user");
    if (!userRaw) {
      navigate("/login");
      return;
    }
    const pid = product?.id || productId;
    if (!pid || submittingReview) return;
    setSubmittingReview(true);
    try {
      await createReview({ product_id: pid, rating: Number(rating), review: (reviewText || "").trim() });
      setReviewSuccess(t("review_submitted", { defaultValue: "تم إضافة التقييم" }));
      setRating(0);
      setReviewText("");
    } catch (err) {
      setReviewError(err?.response?.data?.message || err?.message || t("review_submit_failed", { defaultValue: "فشل إرسال التقييم" }));
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleOpenAddToCart = (prod) => {
    setAddCartProduct(prod || product);
    setAddCartOpen(true);
  };
  const handleCloseAddToCart = () => {
    setAddCartOpen(false);
    setAddCartProduct(null);
  };
  const openShareDialog = () => setShareOpen(true);
  const closeShareDialog = () => setShareOpen(false);

  useEffect(() => {
    if (!productId) return;
    let mounted = true;
    const fetch = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await getProductInfo(productId);
        const full = res || {};
        const prod = full?.data?.data || full?.data || full;
        const reviewSum = full?.reviews_summary || prod?.reviews_summary || null;
        const relProds = full?.releated_products || full?.related_products || prod?.releated_products || prod?.related_products || [];
        const relCat = full?.reated_category || full?.related_category || full?.related_category || prod?.categroy || null;
        if (mounted) {
          setProduct(prod || null);
          setReviewsSummary(reviewSum || null);
          setRelatedProducts(Array.isArray(relProds) ? relProds : []);
          setRelatedCategory(relCat || null);
        }
      } catch (err) {
        if (mounted) setError(err?.response?.data?.message || err?.message || "فشل جلب بيانات المنتج.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, [productId]);

  const getSelectedCountryId = () => {
    try {
      const v = localStorage.getItem("country_id");
      return v !== null ? String(v) : null;
    } catch {
      return null;
    }
  };

  const getPriceInfoForProduct = (prod) => {
    if (!prod) return null;
    const countryId = getSelectedCountryId();
    const curr = prod.currencies || prod.Currencies || [];
    let entry = null;
    if (countryId) entry = curr.find((c) => String(c.country_id) === String(countryId));
    if (!entry && curr.length) entry = curr[0];
    if (!entry) {
      if (prod.price || prod.price_after_discount) {
        return {
          price: prod.price ? String(prod.price) : null,
          price_after_discount: parseFloat(prod.price_after_discount) > 0 ? String(prod.price_after_discount) : null,
        };
      }
      return null;
    }
    return {
      price: entry.price ? String(entry.price) : null,
      price_after_discount: parseFloat(entry.price_after_discount) > 0 ? String(entry.price_after_discount) : null,
    };
  };

  const formatPrice = (p) => (p == null ? "" : p);
  
  const resolveImageSrc = (item) => {
    if (!item) return null;
    const src = (typeof item === "string") ? item : (item?.url || item?.image || "");
    if (!src) return null;
    try {
      return getImageUrl(src);
    } catch {
      return MEDIA_BASE_URL ? `${MEDIA_BASE_URL}${src}` : src;
    }
  };
  
  const formatDate = (iso) => {
    if (!iso) return "-";
    try { 
      return new Date(iso).toLocaleDateString(lang, { year: 'numeric', month: 'short', day: 'numeric' }); 
    } catch { 
      return iso; 
    }
  };

  if (!productId) {
    return <Alert severity="warning">{t("product_id_missing", { defaultValue: "معرف المنتج غير محدد." })}</Alert>;
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress size={56} sx={{ color: "primary.main" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>
      </Container>
    );
  }

  if (!product) return null;

  const name = isAr ? (product.name_ar || product.name_en) : (product.name_en || product.name_ar) || t("product", { defaultValue: "منتج" });
  const desc = isAr ? (product.desc_ar || product.desc_en) : (product.desc_en || product.desc_ar) || "";
  const images = product.images && product.images.length ? product.images : (product.main_photo ? [product.main_photo] : []);
  const sizes = product.sizes || [];
  const colors = product.colors || [];
  const features = product.features || [];
  const reviews = Array.isArray(product.reviews) ? product.reviews : [];
  const priceInfo = getPriceInfoForProduct(product);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: "linear-gradient(90deg, #d5aa2f 0%, #e6c76a 50%, #d5aa2f 100%)",
        },
      }}
    >
      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 6 } }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 3, px: 1 }}
        >
          <Typography
            component={RouterLink}
            to="/"
            sx={{
              color: "text.secondary",
              textDecoration: "none",
              "&:hover": { color: "primary.main" },
              fontWeight: 600,
            }}
          >
            {t("home", { defaultValue: "الرئيسية" })}
          </Typography>
          <Typography
            component={RouterLink}
            to="/products"
            sx={{
              color: "text.secondary",
              textDecoration: "none",
              "&:hover": { color: "primary.main" },
              fontWeight: 600,
            }}
          >
            {t("products", { defaultValue: "المنتجات" })}
          </Typography>
          {product.categroy?.id && (
            <Typography
              component={RouterLink}
              to={`/category/${product.categroy.id}`}
              sx={{
                color: "text.secondary",
                textDecoration: "none",
                "&:hover": { color: "primary.main" },
                fontWeight: 600,
              }}
            >
              {isAr ? (product.categroy.name_ar || product.categroy.name_en) : (product.categroy.name_en || product.categroy.name_ar)}
            </Typography>
          )}
          <Typography color="text.primary" fontWeight={700}>
            {name}
          </Typography>
        </Breadcrumbs>

        <Grid container spacing={4}>
          {/* Image Gallery */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "grey.100",
                p: 3,
                bgcolor: "#fafafa",
                position: "sticky",
                top: 80,
              }}
            >
              {images.length ? (
                <Box
                  sx={{
                    position: "relative",
                    borderRadius: 3,
                    overflow: "hidden",
                    cursor: "zoom-in",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      border: "3px solid transparent",
                      borderRadius: 3,
                      transition: "border-color 0.3s ease",
                    },
                    "&:hover::after": {
                      borderColor: "primary.main",
                    },
                  }}
                  onClick={() => openLightbox(0)}
                >
                  <img
                    src={resolveImageSrc(images[0]) || "/placeholder.png"}
                    alt={name}
                    style={{
                      width: "100%",
                      objectFit: "contain",
                      maxHeight: 480,
                      borderRadius: 12,
                    }}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    height: 480,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "grey.100",
                    borderRadius: 3,
                  }}
                >
                  <Typography color="text.secondary" fontWeight={600}>
                    {t("no_image", { defaultValue: "لا توجد صورة" })}
                  </Typography>
                </Box>
              )}

              {images.length > 1 && (
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{
                    mt: 3,
                    overflowX: "auto",
                    pb: 1,
                    "&::-webkit-scrollbar": {
                      height: 6,
                    },
                  }}
                >
                  {images.map((img, idx) => (
                    <Avatar
                      key={idx}
                      variant="rounded"
                      src={resolveImageSrc(img) || undefined}
                      alt={`${name}-${idx}`}
                      sx={{
                        width: 80,
                        height: 80,
                        cursor: "pointer",
                        border: "2px solid",
                        borderColor: "grey.200",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          borderColor: "primary.main",
                          transform: "scale(1.1)",
                          boxShadow: "0 4px 16px rgba(213, 170, 47, 0.25)",
                        },
                      }}
                      onClick={() => openLightbox(idx)}
                    />
                  ))}
                </Stack>
              )}
            </Paper>
          </Grid>

          {/* Product Details */}
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              {/* Title & Meta */}
              <Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    mb: 2,
                    lineHeight: 1.2,
                    background: "linear-gradient(135deg, #1a1a1a 0%, #6b6b6b 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {name}
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1, mb: 2 }}>
                  {product.categroy?.id && (
                    <Chip
                      component={RouterLink}
                      to={`/category/${product.categroy.id}`}
                      label={isAr ? (product.categroy.name_ar || product.categroy.name_en) : (product.categroy.name_en || product.categroy.name_ar)}
                      clickable
                      sx={{
                        bgcolor: "primary.50",
                        color: "primary.dark",
                        fontWeight: 700,
                        "&:hover": {
                          bgcolor: "primary.light",
                        },
                      }}
                    />
                  )}
                  <Chip
                    icon={<VerifiedIcon />}
                    label={product.status ? t("status_active", { defaultValue: "نشط" }) : t("status_inactive", { defaultValue: "غير نشط" })}
                    sx={{
                      bgcolor: product.status ? "success.50" : "error.50",
                      color: product.status ? "success.dark" : "error.dark",
                      fontWeight: 700,
                    }}
                  />
                </Stack>

                {desc && (
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 2, lineHeight: 1.8 }}
                  >
                    {desc}
                  </Typography>
                )}
              </Box>

              {/* Price Card */}
              {priceInfo && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: "2px solid",
                    borderColor: "primary.light",
                    bgcolor: "primary.50",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: -30,
                      right: -30,
                      width: 150,
                      height: 150,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                      opacity: 0.1,
                    }}
                  />

                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5, position: "relative", zIndex: 1 }}>
                    <LocalOfferIcon sx={{ color: "primary.main", fontSize: 24 }} />
                    <Typography variant="h6" fontWeight={800} color="primary.dark">
                      {t("price", "السعر")}
                    </Typography>
                  </Stack>

                  <Box sx={{ position: "relative", zIndex: 1 }}>
                    {priceInfo.price_after_discount ? (
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            textDecoration: "line-through",
                            color: "text.disabled",
                            fontWeight: 600,
                            mb: 0.5,
                          }}
                        >
                          {formatPrice(priceInfo.price)} {isAr ? "ج.م" : "EGP"}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Typography
                            variant="h3"
                            sx={{
                              color: "primary.main",
                              fontWeight: 800,
                            }}
                          >
                            {formatPrice(priceInfo.price_after_discount)} {isAr ? "ج.م" : "EGP"}
                          </Typography>
                          <Chip
                            label={`-${Math.round((1 - parseFloat(priceInfo.price_after_discount) / parseFloat(priceInfo.price)) * 100)}%`}
                            sx={{
                              bgcolor: "error.main",
                              color: "#fff",
                              fontWeight: 800,
                              fontSize: "1rem",
                              height: 36,
                            }}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <Typography
                        variant="h3"
                        sx={{
                          color: "primary.main",
                          fontWeight: 800,
                        }}
                      >
                        {formatPrice(priceInfo.price)} {isAr ? "ج.م" : "EGP"}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              )}

              {/* Sizes & Colors */}
              {(sizes.length > 0 || colors.length > 0) && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "grey.100",
                  }}
                >
                  {sizes.length > 0 && (
                    <Box sx={{ mb: colors.length > 0 ? 3 : 0 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, color: "primary.dark" }}>
                        {t('sizes', 'المقاسات')}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                        {sizes.map((s) => (
                          <Chip
                            key={s.id || s.key}
                            label={s.size || s.key}
                            sx={{
                              bgcolor: "primary.50",
                              color: "primary.dark",
                              fontWeight: 700,
                              fontSize: "0.875rem",
                              height: 36,
                              px: 2,
                              "&:hover": {
                                bgcolor: "primary.light",
                                transform: "scale(1.05)",
                              },
                              transition: "all 0.2s ease",
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {colors.length > 0 && (
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, color: "primary.dark" }}>
                        {t('colors', 'الألوان')}
                      </Typography>
                      <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ gap: 1.5 }}>
                        {colors.map((c) => (
                          <Box
                            key={c.id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              px: 2,
                              py: 1,
                              borderRadius: 2,
                              border: "2px solid",
                              borderColor: "grey.200",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                borderColor: "primary.main",
                                transform: "scale(1.05)",
                                bgcolor: "primary.50",
                              },
                            }}
                          >
                            {c.code && (
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: "50%",
                                  bgcolor: c.code,
                                  border: "3px solid white",
                                  boxShadow: "0 0 0 1px rgba(0,0,0,0.1)",
                                }}
                              />
                            )}
                            <Typography variant="body2" fontWeight={700}>
                              {isAr ? (c.color_ar || c.color_en) : (c.color_en || c.color_ar) || `Color ${c.id}`}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Paper>
              )}

              {/* Action Buttons */}
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<ShoppingCartIcon />}
                  onClick={() => handleOpenAddToCart(product)}
                  sx={{
                    flex: 1,
                    py: 1.8,
                    fontSize: "1rem",
                    fontWeight: 800,
                    borderRadius: 3,
                    textTransform: "none",
                    background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                    boxShadow: "0 4px 16px rgba(213, 170, 47, 0.35)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #b08c26 0%, #d5aa2f 100%)",
                      boxShadow: "0 6px 24px rgba(213, 170, 47, 0.45)",
                    },
                  }}
                >
                  {t("add_to_cart")}
                </Button>

                <IconButton
                  onClick={handleSave}
                  disabled={saving || saved}
                  sx={{
                    border: "2px solid",
                    borderColor: saved ? "error.main" : "grey.300",
                    bgcolor: saved ? "error.50" : "background.paper",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: "error.main",
                      bgcolor: "error.50",
                      transform: "scale(1.1)",
                    },
                  }}
                >
                  {saving ? (
                    <CircularProgress size={24} />
                  ) : saved ? (
                    <FavoriteIcon sx={{ color: "error.main" }} />
                  ) : (
                    <FavoriteBorderIcon />
                  )}
                </IconButton>

                <IconButton
                  onClick={openShareDialog}
                  sx={{
                    border: "2px solid",
                    borderColor: "grey.300",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: "primary.main",
                      bgcolor: "primary.50",
                      transform: "scale(1.1)",
                    },
                  }}
                >
                  <ShareIcon />
                </IconButton>
              </Stack>

              {saveError && (
                <Alert severity="error" sx={{ borderRadius: 3 }}>
                  {saveError}
                </Alert>
              )}

              {/* Reviews Summary */}
              {(reviewsSummary || product.reviews_summary) && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "grey.100",
                  }}
                >
                  <Stack direction="row" spacing={3} alignItems="center">
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="h2" fontWeight={800} color="primary.main">
                        {(reviewsSummary?.average_rating ?? product.reviews_summary?.average_rating ?? "0.0")}
                      </Typography>
                      <Rating
                        value={Number(reviewsSummary?.average_rating ?? product.reviews_summary?.average_rating ?? 0)}
                        readOnly
                        precision={0.1}
                        sx={{ mt: 1 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, fontWeight: 600 }}>
                        {(reviewsSummary?.total_reviews ?? product.reviews_summary?.total_reviews ?? 0)} {t("reviews", "تقييم")}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              )}

              {/* Add Review */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "grey.100",
                  bgcolor: "grey.50",
                }}
              >
                <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: "primary.dark" }}>
                  {t("add_your_review", { defaultValue: "أضف تقييمك" })}
                </Typography>

                <Stack spacing={2}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {t("your_rating", "تقييمك")}:
                    </Typography>
                    <Rating
                      value={rating}
                      onChange={(e, val) => setRating(val)}
                      size="large"
                    />
                  </Box>

                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder={t("write_review_placeholder", { defaultValue: "اكتب مراجعتك..." })}
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                    disabled={submittingReview}
                  />

                  <Button
                    variant="contained"
                    onClick={handleSubmitReview}
                    disabled={submittingReview || !rating}
                    sx={{
                      py: 1.2,
                      fontWeight: 700,
                      borderRadius: 2,
                    }}
                  >
                    {submittingReview ? (
                      <CircularProgress size={20} sx={{ color: "white" }} />
                    ) : (
                      t("submit_review", { defaultValue: "أرسل التقييم" })
                    )}
                  </Button>

                  {reviewError && <Alert severity="error">{reviewError}</Alert>}
                  {reviewSuccess && <Alert severity="success">{reviewSuccess}</Alert>}
                </Stack>
              </Paper>

              {/* Reviews List */}
              {reviews.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "grey.100",
                  }}
                >
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: "primary.dark" }}>
                    {t("customer_reviews", "آراء العملاء")}
                  </Typography>

                  <Stack spacing={2}>
                    {reviews.slice(0, 5).map((r) => (
                      <Paper
                        key={r.id}
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: "grey.50",
                          border: "1px solid",
                          borderColor: "grey.100",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                          <Rating value={Number(r.rating)} size="small" readOnly />
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(r.created_at)}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                          {r.review}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                </Paper>
              )}
            </Stack>
          </Grid>
        </Grid>

        {/* Related Products */}
        {relatedProducts && relatedProducts.length > 0 && (
          <Box sx={{ mt: 8 }}>
            <Box
              sx={{
                mb: 4,
                pb: 3,
                borderBottom: "2px solid",
                borderImage: "linear-gradient(90deg, #d5aa2f, #e6c76a, transparent) 1",
              }}
            >
              <Typography
                variant="h4"
                fontWeight={800}
                sx={{
                  background: "linear-gradient(135deg, #d5aa2f 0%, #b08c26 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {t("related_products", "منتجات متعلقة")}
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {relatedProducts.slice(0, 4).map((p) => (
                <Grid item xs={12} sm={6} md={3} key={p.id}>
                  <Card
                    sx={{
                      height: "100%",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 12px 32px rgba(213, 170, 47, 0.15)",
                      },
                    }}
                  >
                    <CardActionArea component={RouterLink} to={`/product/${p.id}`}>
                      <CardMedia
                        component="img"
                        image={getImageUrl(p.main_photo || (p.images && p.images[0] && p.images[0].image) || "") || "/placeholder.png"}
                        alt={isAr ? (p.name_ar || p.name_en) : (p.name_en || p.name_ar)}
                        sx={{ height: 200, objectFit: "cover" }}
                      />
                      <Box sx={{ p: 2 }}>
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {isAr ? (p.name_ar || p.name_en) : (p.name_en || p.name_ar)}
                        </Typography>
                      </Box>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>

      {/* Dialogs */}
      <AddToCartDialog
        product={addCartProduct}
        sizes={addCartProduct?.sizes || []}
        colors={addCartProduct?.colors || []}
        open={addCartOpen}
        onClose={handleCloseAddToCart}
        onAdded={() => {}}
      />

      <ShareUrlDialog
        open={shareOpen}
        onClose={closeShareDialog}
        url={typeof window !== "undefined" ? window.location.href : ""}
      />

      {/* Image Lightbox */}
      <Dialog
        open={lightboxOpen}
        onClose={closeLightbox}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "rgba(0, 0, 0, 0.95)",
            borderRadius: 4,
          },
        }}
      >
        <Box sx={{ position: "relative", p: 2 }}>
          <IconButton
            onClick={closeLightbox}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "white",
              bgcolor: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.2)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          <IconButton
            onClick={prevLightbox}
            sx={{
              position: "absolute",
              left: 16,
              top: "50%",
              transform: "translateY(-50%)",
              color: "white",
              bgcolor: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.2)",
              },
            }}
          >
            <ArrowBackIosIcon />
          </IconButton>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "70vh",
              p: 4,
            }}
          >
            <img
              src={resolveImageSrc(images[lightboxIndex]) || "/placeholder.png"}
              alt={`${name}-${lightboxIndex}`}
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
              }}
            />
          </Box>

          <IconButton
            onClick={nextLightbox}
            sx={{
              position: "absolute",
              right: 16,
              top: "50%",
              transform: "translateY(-50%)",
              color: "white",
              bgcolor: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.2)",
              },
            }}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        </Box>
      </Dialog>
    </Box>
  );
}
